import React, { useEffect, useMemo } from "react"
import { map } from "lodash"
import { useDispatch } from "react-redux"
import { QueryObserverBaseResult } from "@tanstack/react-query"
import { Creators } from "model/apiWrapper"
import { combineTanStackStatuses, DataStatus, OK } from "helpers/data-statuses"
import useCombinedQueries from "hooks/combine-queries"
import { RestQueryConfig } from "./types"

const DEFAULT_METHOD = "GET"

function isFailedQueryResult(
  result: QueryObserverBaseResult | DataStatus | undefined,
): result is QueryObserverBaseResult {
  return typeof result === "object" && result?.status === "error"
}

function combineQueryResults(results: (QueryObserverBaseResult | DataStatus | undefined)[]) {
  const status = combineTanStackStatuses(results)
  const data = status === OK ? map(results, "data") : null
  // TODO: only the first error is surfaced. If multiple queries fail simultaneously,
  // subsequent errors are silently dropped. Consider exposing an errors[] array if
  // consumers ever need to react to each failure independently.
  const firstError = results.find(isFailedQueryResult)?.error ?? null
  return { status, data, firstError }
}

/**
 * Generic REST data integrator that provides type-safe data fetching and rendering
 *
 * @template TData - The type of data returned by queries (after select/transform)
 */
interface RestDataIntegratorProps<TData = unknown> {
  queries: RestQueryConfig<TData>[]
  Renderer: React.ComponentType<{ status: DataStatus, data: TData | null } & Record<string, unknown>>
  rendererProps?: Record<string, unknown>
  dataTransform?: (data: TData[]) => TData | null
  onStatusChange?: (status: DataStatus) => void
  onError?: (error: unknown) => void
}

export default function RestDataIntegrator<TData = unknown>({
  queries,
  Renderer,
  rendererProps,
  dataTransform,
  onStatusChange,
  onError,
}: RestDataIntegratorProps<TData>) {
  const dispatch = useDispatch()

  const restQueries = useMemo(() => {
    return map(queries, ({ key, url, method, payload, select, staleTime, gcTime }) => ({
      queryKey: key,
      queryFn: () => new Promise((resolve, reject) => {
        const normalizedMethod = (method || DEFAULT_METHOD).toUpperCase()
        if (normalizedMethod === "GET") {
          dispatch(Creators.get(url, resolve, reject))
          return
        }
        if (normalizedMethod === "POST") {
          dispatch(Creators.post(url, payload, resolve, reject))
          return
        }
        reject(new Error(`Unsupported method: ${normalizedMethod}`))
      }),
      select,
      ...(staleTime !== undefined && { staleTime }),
      ...(gcTime !== undefined && { gcTime }),
    }))
  }, [queries, dispatch])

  const { status, data, firstError } = useCombinedQueries(restQueries, combineQueryResults)

  useEffect(() => {
    onStatusChange?.(status)
  }, [status, onStatusChange])

  useEffect(() => {
    if (firstError && onError) {
      onError(firstError)
    }
  }, [firstError, onError])

  const transformedData = useMemo(() => {
    if (status === OK && typeof dataTransform === "function") {
      return dataTransform(data as TData[])
    }
    return data as TData | null
  }, [status, data, dataTransform])

  return <Renderer status={status} data={transformedData} {...rendererProps} />
}
